'use client';

import {
  buildContext,
  buildCriterionExpression,
  buildSort,
} from '@coveo/atomic-react';
import { CoveoSearchHub } from '@lib/coveo/coveoConstants';
import {
  POPULARITY_SORT,
  RELEVANCE_SORT,
  RELEVANCY_CRITERION_EXPRESSION,
  applyRankingContext,
  getSortCriteriaForSearchHub,
  getSortValueForRankingContext,
} from '@lib/coveo/sortCriteria';
import { useController } from '@lib/hooks/useController';
import { useCallback, useEffect, useMemo } from 'react';
import { useEngine } from '../EngineContext';

export const useSearchV2Sort = () => {
  const eng = useEngine();
  const isListingPage = eng.state.searchHub === CoveoSearchHub.LISTING;
  const defaultSort = isListingPage ? POPULARITY_SORT : RELEVANCE_SORT;
  const sortCriteria = getSortCriteriaForSearchHub(eng.state.searchHub);

  const sortController = useMemo(
    () =>
      buildSort(eng, {
        initialState: { criterion: defaultSort.criterion },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eng]
  );
  const coveoContext = useMemo(() => buildContext(eng), [eng]);
  const sortState = useController(sortController);
  const contextState = useController(coveoContext);

  // Set the default ranking context on mount for listing pages
  useEffect(() => {
    if (isListingPage) {
      applyRankingContext(coveoContext, defaultSort.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Falls back to defaultSort for unknown criteria (stale/hand-edited URLs)
  // so the ranking context never drifts out of sync with the displayed sort.
  const fieldSortValue = useMemo(() => {
    if (sortState.sortCriteria === RELEVANCY_CRITERION_EXPRESSION) return null;

    const matched = sortCriteria.find(
      (opt) =>
        buildCriterionExpression(opt.criterion) === sortState.sortCriteria
    );
    return matched?.value ?? defaultSort.value;
  }, [sortState.sortCriteria, sortCriteria, defaultSort.value]);

  // Clear the ranking flags when the URL restores a field sort, which Coveo
  // applies straight to the engine without going through handleSort.
  useEffect(() => {
    if (fieldSortValue) {
      applyRankingContext(coveoContext, fieldSortValue);
    }
  }, [fieldSortValue, coveoContext]);

  // Derived from engine state rather than per-hook state so that every mounted
  // control — desktop Sort and MobileFilterDrawer both render at all widths —
  // reports the same sort, including across the relevancy-based ranking modes.
  const currentSortValue =
    fieldSortValue ??
    getSortValueForRankingContext(contextState.values, defaultSort.value);

  const handleSort = useCallback(
    (value: string) => {
      const option = sortCriteria.find((opt) => opt.value === value);
      if (!option) return;
      applyRankingContext(coveoContext, value);
      sortController.sortBy(option.criterion);
    },
    [sortController, coveoContext, sortCriteria]
  );

  return { currentSortValue, sortCriteria, handleSort };
};
